import { useState, useMemo } from 'react';
import { useStore } from '@/hooks/useStore';
import { useAuth } from '@/hooks/useAuth';
import {
  getFarmersByHtx, getDebtsByHtx, getLoansByHtx, getSeasonsByHtx, getContractsByHtx,
  getActivePaymentIntentByLoan, getPaymentIntentsByFarmer,
  getHtxInterestRateOverride, getFarmerInterestRateOverride,
  htxConfirmReceipt, complaintPaymentIntent, cancelRepaymentIntent,
} from '@/store';
import { formatCompactCurrency4, formatCompactCurrency, formatDate, formatDateTime, formatVND, todayInputValue } from '@/utils/format';
import { estimateAmountOwed, resolveEffectiveRatePercent, type AccruedInterest } from '@/utils/interest';
import { showToast } from '@/components/base/Toast';
import { EmptyState } from '@/components/base/EmptyState';
import type { Debt, PaymentIntent, PaymentIntentStatus } from '@/types';
import { DebtReminderModal } from '@/components/feature/DebtReminderModal';
import { DEBT_FILTER_CHIPS } from '@/utils/status';
import { MobileMetricCard } from '@/components/base/MobileUi';
import { SearchFilterBar } from '@/components/base/SearchFilterBar';

// The real backend settles a loan in one shot (no partial repayment, no interest/late-fee
// accrual tracked at the loan level) — the amount owed is simply the loan's remaining
// principal (`debt.conNo`). See store/loans.ts's toDebt() for the same rule.

// ─── Helpers ─────────────────────────────────────────────────

type ReminderLevel = 'none' | 't7' | 't3' | 't1' | 'due' | 'overdue';

function getReminderLevel(debt: Debt): ReminderLevel {
  if (debt.status === 'Đã tất toán') return 'none';
  if (debt.status === 'Quá hạn') return 'overdue';
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDate = new Date(debt.ngayDenHan);
  const dueStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
  const diffDays = Math.floor((dueStart.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'overdue';
  if (diffDays === 0) return 'due';
  if (diffDays <= 1) return 't1';
  if (diffDays <= 3) return 't3';
  if (diffDays <= 7) return 't7';
  return 'none';
}

function DebtMetric({ label, value, tone = 'slate' }: { label: string; value: string; tone?: 'slate' | 'emerald' | 'rose' }) {
  const color = tone === 'emerald' ? 'text-agrida-700' : tone === 'rose' ? 'text-rose-700' : 'text-slate-800';
  return <div className="min-w-0 rounded-xl bg-slate-50 p-2.5"><p className="text-[10px] text-slate-400">{label}</p><p className={`mt-0.5 truncate text-xs font-bold ${color}`}>{value}</p></div>;
}

function DebtDetail({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0"><p className="text-[10px] text-slate-400">{label}</p><p className="mt-0.5 text-xs font-semibold text-slate-700 text-safe-normal">{value}</p></div>;
}

const reminderBadge: Record<ReminderLevel, { label: string; bg: string; text: string; icon: string }> = {
  none: { label: '', bg: '', text: '', icon: '' },
  t7: { label: 'Còn 7 ngày', bg: 'bg-sky-100', text: 'text-sky-700', icon: 'ri-calendar-event-line' },
  t3: { label: 'Còn 3 ngày', bg: 'bg-amber-100', text: 'text-amber-700', icon: 'ri-calendar-check-line' },
  t1: { label: 'Còn 1 ngày', bg: 'bg-orange-100', text: 'text-orange-700', icon: 'ri-alarm-warning-line' },
  due: { label: 'Hôm nay!', bg: 'bg-red-100', text: 'text-red-700', icon: 'ri-error-warning-line' },
  overdue: { label: 'Quá hạn', bg: 'bg-red-200', text: 'text-red-800', icon: 'ri-timer-flash-line' },
};

// ─── PI helpers ──────────────────────────────────────────────

function getDebtPI(debt: Debt): PaymentIntent | undefined {
  return getActivePaymentIntentByLoan(debt.farmerId, debt.advanceRequestId || debt.id);
}

function mapPIStatusToDebtDisplayStatus(piStatus: PaymentIntentStatus): string {
  const map: Record<PaymentIntentStatus, string> = {
    qr_created: 'Chờ nông hộ xác nhận',
    farmer_confirmed_transfer: 'Chờ HTX xác nhận',
    waiting_bank_credit: 'Chờ HTX xác nhận',
    bank_credit_received: 'Chờ HTX xác nhận',
    reconciled: 'Chờ HTX xác nhận',
    waiting_htx_confirmation: 'Chờ HTX xác nhận',
    settled: 'Đã tất toán',
    reconcile_failed: 'Đối soát lỗi',
    cancelled: 'Đã hủy',
    complaint: 'Khiếu nại',
  };
  return map[piStatus] || piStatus;
}

const PI_CARD_STATUS_CLASS: Record<string, string> = {
  'Chờ HTX xác nhận': 'bg-indigo-100 text-indigo-700',
  'Đã tất toán': 'bg-agrida-100 text-agrida-700',
  'Đối soát lỗi': 'bg-red-100 text-red-700',
  'Đã hủy': 'bg-slate-100 text-slate-500',
  'Khiếu nại': 'bg-rose-100 text-rose-700',
  'Chờ nông hộ xác nhận': 'bg-orange-100 text-orange-700',
};

function getWrappedDebtStatus(debt: Debt): string {
  const pi = getDebtPI(debt);
  if (pi) return mapPIStatusToDebtDisplayStatus(pi.status);
  return debt.status;
}

function getWrappedDebtStatusClass(debt: Debt): string {
  const pi = getDebtPI(debt);
  if (pi) {
    const display = mapPIStatusToDebtDisplayStatus(pi.status);
    return PI_CARD_STATUS_CLASS[display] || 'bg-slate-100 text-slate-600';
  }
  const map: Record<string, string> = {
    'Đang vay': 'bg-sky-100 text-sky-700', 'Sắp đến hạn': 'bg-amber-100 text-amber-700',
    'Đến hạn': 'bg-orange-100 text-orange-700', 'Quá hạn': 'bg-red-100 text-red-700',
    'Đã tất toán': 'bg-agrida-100 text-agrida-700',
  };
  return map[debt.status] || 'bg-slate-100 text-slate-600';
}

// ─── Filter chip config (centralized from status.ts) ─────────
function mapDebtToFilterKey(debt: Debt): string {
  const pi = getDebtPI(debt);
  if (pi) {
    for (const chip of DEBT_FILTER_CHIPS) {
      if (chip.piMatch && (chip.piMatch as readonly string[]).includes(pi.status)) {
        return chip.key;
      }
    }
  }
  for (const chip of DEBT_FILTER_CHIPS) {
    if (chip.statusMatch && (chip.statusMatch as readonly string[]).includes(debt.status)) {
      return chip.key;
    }
  }
  return 'active';
}

// ─── PI Status utilities ─────────────────────────────────────
const PI_STATUS_LABEL: Record<PaymentIntentStatus, string> = {
  qr_created: 'Đã tạo QR',
  farmer_confirmed_transfer: 'Nông hộ đã xác nhận CK',
  waiting_bank_credit: 'Chờ HTX xác nhận (dữ liệu cũ)',
  bank_credit_received: 'Chờ HTX xác nhận (dữ liệu cũ)',
  reconciled: 'HTX đã ghi nhận',
  waiting_htx_confirmation: 'Chờ HTX xác nhận',
  settled: 'Đã tất toán',
  reconcile_failed: 'Đối soát lỗi',
  cancelled: 'Đã hủy',
  complaint: 'Khiếu nại thanh toán',
};

function canConfirmSettle(pi: PaymentIntent): boolean {
  return ['farmer_confirmed_transfer', 'waiting_bank_credit', 'bank_credit_received', 'reconciled', 'waiting_htx_confirmation'].includes(pi.status);
}

// ─── Main Component ──────────────────────────────────────────
export function CongNoTab({ onNavigate }: { onNavigate?: (tab: string) => void }) {
  useStore();
  const { user } = useAuth();
  const farmers = getFarmersByHtx(user?.htxId);
  const debts = getDebtsByHtx(user?.htxId);
  const loans = getLoansByHtx(user?.htxId);
  const seasons = getSeasonsByHtx(user?.htxId);
  const contracts = getContractsByHtx(user?.htxId);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [farmerFilter, setFarmerFilter] = useState('Tất cả');
  const [expandedDebtId, setExpandedDebtId] = useState('');

  // Modal states
  const [historyDebt, setHistoryDebt] = useState<Debt | null>(null);
  const [reminderDebt, setReminderDebt] = useState<{ debt: Debt; farmerName: string } | null>(null);
  const [piDetailDebt, setPiDetailDebt] = useState<{ debt: Debt; pi: PaymentIntent } | null>(null);
  const [busyPI, setBusyPI] = useState<string | null>(null);

  const getFarmerName = (id: string) => farmers.find((f) => f.id === id)?.name || id;
  const getSeasonName = (loanId?: string) => {
    if (!loanId) return undefined;
    const loan = loans.find((l) => l.id === loanId);
    if (!loan?.seasonId) return undefined;
    return seasons.find((s) => s.id === loan.seasonId)?.name;
  };
  const getContractInfo = (farmerId: string) => {
    return contracts.find((c) => c.farmerId === farmerId && c.status === 'Đang hiệu lực');
  };
  // A Debt is derived entirely from a Loan (store/loans.ts's toDebt) — this
  // looks the real loan record back up so the history modal can show mục
  // đích/loại vay/mùa vụ/lãi suất instead of only the thin Gốc/Đã trả/Còn nợ
  // figures the Debt shape itself carries.
  const getLoanForDebt = (debt: Debt) => loans.find((l) => l.id === (debt.advanceRequestId || debt.id));
  const htxRateOverride = getHtxInterestRateOverride(user?.htxId);
  // "Lãi tích lũy" — accrued daily from disbursement to settlement (or to
  // today, still-outstanding) at whichever rate applies: farmer override >
  // HTX override > the loan's own stored rate — the same figure a repayment
  // then charges (gốc + lãi, frozen on the PaymentIntent).
  const getAccruedInterest = (debt: Debt): AccruedInterest | null => {
    const loan = getLoanForDebt(debt);
    if (!debt.ngayVay || loan?.laiSuat === undefined) return null;
    const farmerRateOverride = getFarmerInterestRateOverride(debt.farmerId);
    const effectiveRate = resolveEffectiveRatePercent(loan.laiSuat, farmerRateOverride, htxRateOverride);
    return estimateAmountOwed(debt.soTien, effectiveRate, debt.ngayVay, loan.ngayTatToan);
  };

  const filtered = useMemo(() => {
    return debts.filter((d) => {
      if (search) {
        const farmer = farmers.find((f) => f.id === d.farmerId);
        const q = search.toLowerCase();
        if (!farmer?.name.toLowerCase().includes(q) && !d.id.toLowerCase().includes(q) && !(d.maKhoanVay || '').toLowerCase().includes(q)) return false;
      }
      if (farmerFilter !== 'Tất cả' && d.farmerId !== farmerFilter) return false;
      if (activeFilter !== 'all' && mapDebtToFilterKey(d) !== activeFilter) return false;
      return true;
    });
  }, [debts, search, farmerFilter, activeFilter, farmers]);

  // ─── Stats ─────────────────────────────────────────────────
  const stats = useMemo(() => {
    const allPIs = farmers.flatMap((f) => getPaymentIntentsByFarmer(f.id));
    const activePIs = allPIs.filter((p) => p.status !== 'settled' && p.status !== 'cancelled');
    const active = debts.filter((d) => d.status !== 'Đã tất toán');

    return {
      tongDuNo: active.reduce((s, d) => s + d.conNo, 0),
      sapDenHan: active.filter((d) => d.status === 'Sắp đến hạn' || d.status === 'Đến hạn').length,
      quaHan: active.filter((d) => d.status === 'Quá hạn').length,
      choXacNhanTT: activePIs.filter((p) => ['farmer_confirmed_transfer', 'waiting_bank_credit', 'bank_credit_received', 'reconciled', 'waiting_htx_confirmation'].includes(p.status)).length,
      daTatToan: debts.filter((d) => d.status === 'Đã tất toán').length,
      doiSoatLoi: activePIs.filter((p) => p.status === 'reconcile_failed').length,
    };
  }, [debts, farmers]);

  // ─── HTX Confirm Settlement ────────────────────────────────
  const handleConfirmSettlement = async (pi: PaymentIntent, debt: Debt) => {
    setBusyPI(pi.id);
    try {
      await htxConfirmReceipt(pi.id, debt.farmerId);
      showToast(`Đã tất toán khoản vay của ${getFarmerName(debt.farmerId)}`, 'success');
    } catch {
      showToast('Không thể xác nhận tất toán, vui lòng thử lại', 'error');
    } finally {
      setBusyPI(null);
    }
  };

  // ─── Reject / Complaint ────────────────────────────────────
  const handleRejectPI = async (pi: PaymentIntent, debt: Debt) => {
    setBusyPI(pi.id);
    try {
      await cancelRepaymentIntent(pi.id, debt.farmerId, 'HTX từ chối xác nhận');
      showToast('Đã hủy yêu cầu tất toán. Nông hộ có thể tạo lại.', 'info');
    } catch {
      showToast('Không thể hủy, vui lòng thử lại', 'error');
    } finally {
      setBusyPI(null);
    }
  };

  const handleComplaintPI = async (pi: PaymentIntent, debt: Debt) => {
    setBusyPI(pi.id);
    try {
      await complaintPaymentIntent(pi.id, debt.farmerId, 'Cần điều tra giao dịch');
      showToast('Đã đưa vào khiếu nại. Cần xử lý thủ công.', 'warning');
    } catch {
      showToast('Không thể ghi nhận khiếu nại, vui lòng thử lại', 'error');
    } finally {
      setBusyPI(null);
    }
  };

  // ─── Send Reminder ─────────────────────────────────────────
  const handleSendReminder = (debt: Debt) => {
    setReminderDebt({ debt, farmerName: getFarmerName(debt.farmerId) });
  };

  // ─── View PI Detail ────────────────────────────────────────
  const handleViewPI = (debt: Debt) => {
    const pi = getDebtPI(debt);
    if (pi) setPiDetailDebt({ debt, pi });
  };

  return (
    <div>
      {/* Quick stats */}
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <MobileMetricCard label="Tổng dư nợ" value={formatCompactCurrency4(stats.tongDuNo)} caption="Các khoản chưa tất toán" tone="sky" icon="ri-money-cny-circle-line" />
        <MobileMetricCard label="Sắp đến hạn" value={stats.sapDenHan} caption="Cần theo dõi" tone="amber" icon="ri-calendar-event-line" />
        <MobileMetricCard label="Quá hạn" value={stats.quaHan} caption="Cần xử lý" tone={stats.quaHan ? 'rose' : 'slate'} icon="ri-timer-flash-line" />
        <MobileMetricCard label="Chờ xác nhận" value={stats.choXacNhanTT} caption="Nông hộ đã thanh toán" tone="emerald" icon="ri-checkbox-circle-line" />
      </div>

      <div className="mb-3">
        <SearchFilterBar
          value={search}
          onChange={setSearch}
          placeholder="Tìm nông hộ hoặc mã khoản vay"
          selected={activeFilter === 'all' ? '' : activeFilter}
          onSelect={(value) => setActiveFilter(value || 'all')}
          options={[
            { value: '', label: 'Tất cả', count: debts.length },
            ...DEBT_FILTER_CHIPS.filter((chip) => ['active', 'due_soon', 'overdue', 'waiting_htx_confirmation', 'settled'].includes(chip.key)).map((chip) => ({
              value: chip.key,
              label: chip.label,
              count: debts.filter((debt) => mapDebtToFilterKey(debt) === chip.key).length,
              dotClass: chip.key === 'overdue' ? 'bg-rose-500' : chip.key === 'settled' ? 'bg-agrida-500' : chip.key === 'due_soon' ? 'bg-amber-500' : 'bg-sky-500',
            })),
          ]}
        >
          {/* Lọc theo nông hộ — để đối chiếu số liệu công nợ của một nông hộ
              cụ thể với khoản vay/giao dịch của họ ở các màn khác. */}
          <select value={farmerFilter} onChange={(e) => setFarmerFilter(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
            <option value="Tất cả">Tất cả nông hộ</option>
            {farmers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </SearchFilterBar>
      </div>

      {/* Debt list */}
      {filtered.length === 0 ? (
        <EmptyState icon="ri-money-cny-circle-line" title="Không có công nợ" description="Chưa có khoản vay nào phù hợp với bộ lọc." />
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filtered.map((debt) => {
            const conPhaiTra = debt.conNo;
            const accrued = getAccruedInterest(debt);
            const displayStatus = getWrappedDebtStatus(debt);
            const displayStatusClass = getWrappedDebtStatusClass(debt);
            const pi = getDebtPI(debt);
            const piStatus = pi?.status;
            const isSettled = debt.status === 'Đã tất toán';
            const reminder = getReminderLevel(debt);
            const isOverdue = debt.status === 'Quá hạn' || reminder === 'overdue';
            const canConfirmSettleNow = !!pi && canConfirmSettle(pi);
            const isReconcileFailed = piStatus === 'reconcile_failed';
            const hasActivePI = !!pi;

            return (
              <div key={debt.id} className={`bg-white rounded-xl border p-4 transition-colors ${isSettled ? 'border-agrida-200 bg-agrida-50/20' : isOverdue ? 'border-red-300 bg-red-50/30' : isReconcileFailed ? 'border-rose-300 bg-rose-50/30' : hasActivePI ? 'border-amber-300 bg-amber-50/20' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-slate-900">{getFarmerName(debt.farmerId)}</h3>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${displayStatusClass}`}>{displayStatus}</span>
                      {hasActivePI && (
                        <span className="text-[10px] font-mono bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{pi!.id}</span>
                      )}
                      {reminder !== 'none' && (
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${reminderBadge[reminder].bg} ${reminderBadge[reminder].text}`}>
                          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${reminderBadge[reminder].icon} text-xs`} /></span>
                          {reminderBadge[reminder].label}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {debt.maKhoanVay && <span>Mã KV: {debt.maKhoanVay} · </span>}
                      Đến hạn: {formatDate(debt.ngayDenHan)}
                      {debt.kyHan && <span> · Kỳ hạn: {debt.kyHan}</span>}
                    </p>
                    {(getSeasonName(debt.advanceRequestId) || getContractInfo(debt.farmerId)) && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        {getSeasonName(debt.advanceRequestId) && <span>Mùa vụ: {getSeasonName(debt.advanceRequestId)}</span>}
                        {getSeasonName(debt.advanceRequestId) && getContractInfo(debt.farmerId) && <span> · </span>}
                        {getContractInfo(debt.farmerId) && (
                          <button type="button" onClick={() => onNavigate?.('loan')} className="text-agrida-600 underline">
                            HĐ: {getContractInfo(debt.farmerId)?.soHopDong}
                          </button>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-bold ${isSettled ? 'text-agrida-700' : conPhaiTra > 0 ? 'text-red-700' : 'text-slate-900'}`}>
                      {isSettled ? '0' : formatCompactCurrency4(conPhaiTra)}
                    </p>
                    <p className="text-xs text-slate-400">Còn phải trả (gốc)</p>
                    {accrued && !isSettled && conPhaiTra > 0 && (
                      <p className="text-[10px] text-amber-600 mt-0.5">+{formatCompactCurrency4(accrued.accruedVnd)} lãi ({accrued.days} ngày)</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <DebtMetric label="Gốc" value={formatCompactCurrency4(debt.soTien)} />
                  <DebtMetric label="Đã trả" value={formatCompactCurrency4(debt.daTra)} tone="emerald" />
                  <DebtMetric label="Còn nợ (gốc)" value={formatCompactCurrency4(conPhaiTra)} tone={conPhaiTra > 0 ? 'rose' : 'emerald'} />
                  <DebtMetric
                    label={`Lãi tích lũy (${accrued?.ratePercentPerYear ?? 0}%/năm)`}
                    value={accrued ? formatCompactCurrency4(accrued.accruedVnd) : '—'}
                    tone={accrued && accrued.accruedVnd > 0 ? 'rose' : 'slate'}
                  />
                </div>
                {expandedDebtId === debt.id && <div className="mt-3 grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3"><DebtDetail label="Ngày vay" value={debt.ngayVay ? formatDate(debt.ngayVay) : 'Chưa có'} />{pi?.bankTransactionCode && <DebtDetail label="Mã giao dịch" value={pi.bankTransactionCode} />}</div>}
                {conPhaiTra > 0 && (
                  <div className="mt-2.5">
                    <div className="flex justify-between text-[10px] mb-1">
                      <span className="text-slate-400">Tiến độ: {debt.daTra > 0 ? ((debt.daTra / debt.soTien) * 100).toFixed(1) : '0'}%</span>
                      <span className="text-slate-500">{formatCompactCurrency4(debt.daTra)} / {formatCompactCurrency4(debt.soTien)}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-agrida-500 rounded-full transition-all" style={{ width: `${Math.min((debt.daTra / debt.soTien) * 100, 100)}%` }} />
                    </div>
                  </div>
                )}
                {/* Actions */}
                <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex flex-wrap gap-1.5">
                  {!isSettled && (
                    <>
                      {/* Send reminder */}
                      {(isOverdue || reminder === 'due' || reminder === 't1' || reminder === 't3' || reminder === 't7') && (
                        <button type="button" onClick={() => handleSendReminder(debt)} className={`text-[11px] px-3 py-2 rounded-md font-medium transition-colors whitespace-nowrap flex items-center gap-1 min-h-[36px] ${isOverdue ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}`}>
                          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-notification-3-line" /></span>
                          {isOverdue ? 'Cảnh báo quá hạn' : 'Gửi nhắc trả'}
                        </button>
                      )}

                      {/* View PI Detail */}
                      {hasActivePI && (
                        <button type="button" onClick={() => handleViewPI(debt)} className="text-[11px] bg-sky-600 hover:bg-sky-700 text-white px-3 py-2 rounded-md font-medium transition-colors whitespace-nowrap flex items-center gap-1 min-h-[36px]">
                          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-eye-line" /></span>
                          Xem PaymentIntent
                        </button>
                      )}

                      {/* Confirm Settlement */}
                      {canConfirmSettleNow && (
                        <button type="button" onClick={() => handleConfirmSettlement(pi!, debt)} disabled={busyPI === pi!.id} className={`text-[11px] px-3 py-2 rounded-md font-medium transition-colors whitespace-nowrap flex items-center gap-1 min-h-[36px] ${busyPI === pi!.id ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-agrida-600 hover:bg-agrida-700 text-white'}`}>
                          {busyPI === pi!.id ? (
                            <><span className="w-3.5 h-3.5 flex items-center justify-center animate-spin"><i className="ri-loader-4-line" /></span>Đang xử lý...</>
                          ) : (
                            <><span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-check-double-line" /></span>Xác nhận tất toán</>
                          )}
                        </button>
                      )}

                      {/* Reject / Complaint for failed PI */}
                      {isReconcileFailed && (
                        <>
                          <button type="button" onClick={() => handleComplaintPI(pi!, debt)} disabled={busyPI === pi!.id} className="text-[11px] bg-rose-600 hover:bg-rose-700 text-white px-3 py-2 rounded-md font-medium transition-colors whitespace-nowrap flex items-center gap-1 min-h-[36px] disabled:opacity-60">
                            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-alert-line" /></span>
                            Khiếu nại
                          </button>
                          <button type="button" onClick={() => handleRejectPI(pi!, debt)} disabled={busyPI === pi!.id} className="text-[11px] bg-slate-600 hover:bg-slate-700 text-white px-3 py-2 rounded-md font-medium transition-colors whitespace-nowrap flex items-center gap-1 min-h-[36px] disabled:opacity-60">
                            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-close-circle-line" /></span>
                            Từ chối
                          </button>
                        </>
                      )}
                    </>
                  )}
                  <button type="button" onClick={() => setHistoryDebt(debt)} className="text-[11px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-md font-medium transition-colors whitespace-nowrap flex items-center gap-1 min-h-[36px]">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-history-line" /></span>
                    Lịch sử
                  </button>
                  <button type="button" onClick={() => setExpandedDebtId(expandedDebtId === debt.id ? '' : debt.id)} className="ml-auto min-h-[36px] rounded-md px-2 text-[11px] font-medium text-slate-500">{expandedDebtId === debt.id ? 'Thu gọn' : 'Xem thêm'} <i className={expandedDebtId === debt.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── History Modal ─────────────────────────────────── */}
      {historyDebt && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setHistoryDebt(null)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <div><h3 className="text-lg font-semibold text-slate-900">Lịch sử khoản vay</h3><p className="text-xs text-slate-400">{getFarmerName(historyDebt.farmerId)} · {historyDebt.maKhoanVay || historyDebt.id}</p></div>
              <button type="button" onClick={() => setHistoryDebt(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400"><i className="ri-close-line text-lg" /></button>
            </div>
            {(() => {
              const loan = getLoanForDebt(historyDebt);
              const seasonName = getSeasonName(historyDebt.advanceRequestId);
              const contractInfo = getContractInfo(historyDebt.farmerId);
              const accrued = getAccruedInterest(historyDebt);
              const isSettledDebt = historyDebt.status === 'Đã tất toán';
              return (
                <div className="px-6 py-4 space-y-3">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-400 mb-2">Thông tin khoản vay</p>
                    <div className="space-y-1.5 text-sm">
                      {loan?.mucDich && <div className="flex justify-between"><span className="text-slate-500">Mục đích</span><span className="font-medium text-right">{loan.mucDich}</span></div>}
                      {loan?.loai && <div className="flex justify-between"><span className="text-slate-500">Loại vay</span><span className="font-medium">{loan.loai}{loan.loai === 'Vật tư' && loan.tenVatTu ? ` — ${loan.tenVatTu}` : ''}</span></div>}
                      <div className="flex justify-between"><span className="text-slate-500">Gốc</span><span className="font-medium">{formatCompactCurrency4(historyDebt.soTien)}</span></div>
                      {loan?.laiSuat !== undefined && <div className="flex justify-between"><span className="text-slate-500">Lãi suất</span><span className="font-medium">{accrued?.ratePercentPerYear ?? loan.laiSuat}%/năm</span></div>}
                      <div className="flex justify-between"><span className="text-slate-500">Đã trả</span><span className="font-medium text-agrida-700">{formatCompactCurrency4(historyDebt.daTra)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Còn nợ (gốc)</span><span className="font-bold text-red-700">{formatCompactCurrency4(historyDebt.conNo)}</span></div>
                      {accrued && (
                        <>
                          <div className="flex justify-between"><span className="text-slate-500">Lãi tích lũy ({accrued.days} ngày)</span><span className="font-bold text-amber-700">{formatCompactCurrency4(accrued.accruedVnd)}</span></div>
                          <div className="flex justify-between border-t border-slate-200 pt-1.5"><span className="text-slate-600 font-medium">{isSettledDebt ? 'Tổng đã tất toán (gốc+lãi)' : 'Tổng phải trả (gốc+lãi)'}</span><span className="font-bold text-slate-900">{formatCompactCurrency4(accrued.totalOwedVnd)}</span></div>
                        </>
                      )}
                      <div className="flex justify-between"><span className="text-slate-500">Trạng thái</span><span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${getWrappedDebtStatusClass(historyDebt)}`}>{getWrappedDebtStatus(historyDebt)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Ngày đến hạn</span><span>{formatDate(historyDebt.ngayDenHan)}</span></div>
                      {seasonName && <div className="flex justify-between"><span className="text-slate-500">Mùa vụ</span><span className="font-medium">{seasonName}</span></div>}
                      {contractInfo && <div className="flex justify-between"><span className="text-slate-500">Hợp đồng</span><span className="font-medium">{contractInfo.soHopDong}</span></div>}
                      {historyDebt.ghiChu && <div className="flex justify-between"><span className="text-slate-500">Ghi chú</span><span className="text-xs">{historyDebt.ghiChu}</span></div>}
                    </div>
                  </div>

                  {/* Timeline: yêu cầu → giải ngân → tất toán — dùng dữ liệu thật
                      từ khoản vay gốc (Loan) thay vì chỉ 2 dòng tĩnh trước đây. */}
                  {loan?.ngayYeuCau && (
                    <div className="border border-slate-200 rounded-xl p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><span className="w-4 h-4 flex items-center justify-center text-slate-600"><i className="ri-file-list-3-line" /></span></div>
                        <div><p className="text-sm font-medium text-slate-900">Yêu cầu vay</p><p className="text-xs text-slate-400">{formatCompactCurrency(historyDebt.soTien)} · {formatDate(loan.ngayYeuCau)}</p></div>
                      </div>
                    </div>
                  )}
                  {historyDebt.ngayVay && (
                    <div className="border border-slate-200 rounded-xl p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-agrida-100 flex items-center justify-center shrink-0"><span className="w-4 h-4 flex items-center justify-center text-agrida-700"><i className="ri-arrow-down-circle-line" /></span></div>
                        <div><p className="text-sm font-medium text-slate-900">Giải ngân</p><p className="text-xs text-slate-400">{formatCompactCurrency(historyDebt.soTien)} · {formatDate(historyDebt.ngayVay)}</p></div>
                      </div>
                    </div>
                  )}
                  {historyDebt.daTra > 0 && (
                    <div className="border border-slate-200 rounded-xl p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center shrink-0"><span className="w-4 h-4 flex items-center justify-center text-sky-700"><i className="ri-arrow-up-circle-line" /></span></div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">Đã thanh toán</p>
                          <p className="text-xs text-slate-400">{formatCompactCurrency(historyDebt.daTra)}{loan?.ngayTatToan ? ` · ${formatDate(loan.ngayTatToan)}` : ''}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4">
              <button type="button" onClick={() => setHistoryDebt(null)} className="w-full px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors">Đóng</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── PaymentIntent Detail Modal ────────────────────── */}
      {piDetailDebt && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPiDetailDebt(null)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <div><h3 className="text-lg font-semibold text-slate-900">PaymentIntent</h3><p className="text-xs text-slate-400 font-mono">{piDetailDebt.pi.id}</p></div>
              <button type="button" onClick={() => setPiDetailDebt(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className={`rounded-xl p-3 border ${PI_CARD_STATUS_CLASS[mapPIStatusToDebtDisplayStatus(piDetailDebt.pi.status)] || ''}`}>
                <p className="text-xs font-semibold">{PI_STATUS_LABEL[piDetailDebt.pi.status]}</p>
              </div>

              <div className="bg-slate-50 rounded-xl p-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Mã khoản vay</span><span className="font-medium">{piDetailDebt.pi.maKhoanVay || piDetailDebt.pi.debtId}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Số tiền tất toán</span><span className="font-bold text-rose-700">{formatVND(piDetailDebt.pi.soTien)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Nội dung CK</span><span className="font-mono text-[10px]">{piDetailDebt.pi.noiDungChuyenKhoan}</span></div>
                {piDetailDebt.pi.farmerConfirmedAt && <div className="flex justify-between"><span className="text-slate-500">Nông hộ xác nhận</span><span>{formatDateTime(piDetailDebt.pi.farmerConfirmedAt)}</span></div>}
                {piDetailDebt.pi.htxConfirmedAt && <div className="flex justify-between"><span className="text-slate-500">HTX xác nhận</span><span>{formatDateTime(piDetailDebt.pi.htxConfirmedAt)}</span></div>}
                {piDetailDebt.pi.bankTransactionCode && <div className="flex justify-between"><span className="text-slate-500">Mã giao dịch</span><span className="font-mono text-agrida-700">{piDetailDebt.pi.bankTransactionCode}</span></div>}
              </div>

              {piDetailDebt.pi.reconcileError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-xs text-red-700 font-medium mb-1">Lỗi đối soát</p>
                  <p className="text-[11px] text-red-600">{piDetailDebt.pi.reconcileError}</p>
                </div>
              )}

              {piDetailDebt.pi.status === 'settled' && (
                <div className="bg-agrida-50 border border-agrida-200 rounded-xl p-3">
                  <p className="text-xs text-agrida-700">Đã tất toán thành công ngày {piDetailDebt.pi.settledAt ? formatDateTime(piDetailDebt.pi.settledAt) : '—'}</p>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex gap-3 flex-wrap">
              {canConfirmSettle(piDetailDebt.pi) && (
                <button type="button" onClick={() => { const { pi, debt } = piDetailDebt; setPiDetailDebt(null); handleConfirmSettlement(pi, debt); }} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-agrida-600 hover:bg-agrida-700 transition-colors whitespace-nowrap">
                  Xác nhận tất toán
                </button>
              )}
              <button type="button" onClick={() => setPiDetailDebt(null)} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap">
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Debt Reminder Modal ────────────────────────────── */}
      {reminderDebt && (
        <DebtReminderModal
          open={true}
          onClose={() => setReminderDebt(null)}
          debt={reminderDebt.debt}
          farmerName={reminderDebt.farmerName}
          htxName="HTX Hòa Phát"
        />
      )}
    </div>
  );
}
